import { getDriveConnectUrl, getSessionHeaders } from '@/lib/cloudflare/auth-client';

const CF_WORKER_URL = import.meta.env.VITE_CLOUDFLARE_WORKER_URL || '';
const API_BASE = CF_WORKER_URL ? `${CF_WORKER_URL}/api` : '/api';
const PENDING_DRIVE_SAVE_KEY = 'wb_pending_drive_save';
const SAVED_DRIVE_ATTACHMENTS_KEY = 'wb_saved_drive_attachments';
const MAX_PENDING_AGE_MS = 30 * 60 * 1000;
const MAX_SAVED_ATTACHMENT_CACHE_ENTRIES = 500;

export interface SaveAttachmentToDriveInput {
  emailId: string;
  attachmentId: string;
  filename: string;
  mimeType?: string;
  size?: number;
  threadId?: string;
}

export interface DriveSaveResult {
  alreadySaved: boolean;
  fileId: string;
  folderId: string;
  folderName: string;
  filename: string;
  webViewLink?: string | null;
}

export interface PendingDriveSave extends SaveAttachmentToDriveInput {
  createdAt: number;
}

export interface CachedDriveAttachmentSave {
  webViewLink: string | null;
  savedAt: number;
}

type SavedDriveAttachmentCacheEntry = CachedDriveAttachmentSave & {
  emailId: string;
  attachmentId: string;
  filename: string;
  mimeType?: string;
  size?: number;
  threadId?: string;
};

type SavedDriveAttachmentCache = Record<string, SavedDriveAttachmentCacheEntry>;

export class DriveClientError extends Error {
  status?: number;
  code?: string;

  constructor(message: string, status?: number, code?: string) {
    super(message);
    this.name = 'DriveClientError';
    this.status = status;
    this.code = code;
  }
}

function getCurrentReturnUrl(): string {
  return `${window.location.pathname}${window.location.search}`;
}

function normalizeFilename(filename: string): string {
  return filename.trim().toLowerCase();
}

function normalizeMimeType(mimeType?: string): string {
  return (mimeType || '').trim().toLowerCase();
}

function getExactSaveCacheKey(input: Pick<SaveAttachmentToDriveInput, 'emailId' | 'attachmentId'>): string {
  return `exact:${input.emailId}:${input.attachmentId}`;
}

function getThreadFingerprintKey(input: SaveAttachmentToDriveInput): string | null {
  if (!input.threadId) {
    return null;
  }

  return [
    'thread',
    input.threadId,
    normalizeFilename(input.filename),
    input.size ?? 'unknown',
    normalizeMimeType(input.mimeType) || 'unknown',
  ].join(':');
}

function loadSavedDriveAttachmentCache(): SavedDriveAttachmentCache {
  if (typeof window === 'undefined') {
    return {};
  }

  const rawValue = localStorage.getItem(SAVED_DRIVE_ATTACHMENTS_KEY);
  if (!rawValue) {
    return {};
  }

  try {
    const parsed = JSON.parse(rawValue) as SavedDriveAttachmentCache;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    localStorage.removeItem(SAVED_DRIVE_ATTACHMENTS_KEY);
    return {};
  }
}

function persistSavedDriveAttachmentCache(cache: SavedDriveAttachmentCache): void {
  if (typeof window === 'undefined') {
    return;
  }

  const entries = Object.entries(cache)
    .sort(([, a], [, b]) => b.savedAt - a.savedAt)
    .slice(0, MAX_SAVED_ATTACHMENT_CACHE_ENTRIES);

  localStorage.setItem(
    SAVED_DRIVE_ATTACHMENTS_KEY,
    JSON.stringify(Object.fromEntries(entries))
  );
}

function parseDriveError(response: Response, data: any): never {
  throw new DriveClientError(
    data?.error?.message || data?.message || 'Failed to save attachment to Drive',
    response.status,
    data?.error?.code || data?.code
  );
}

export async function saveAttachmentToDrive(input: SaveAttachmentToDriveInput): Promise<DriveSaveResult> {
  const response = await fetch(
    `${API_BASE}/drive/attachments/${encodeURIComponent(input.emailId)}/${encodeURIComponent(input.attachmentId)}/save`,
    {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...getSessionHeaders(),
      },
      body: JSON.stringify({
        filename: input.filename,
        mimeType: input.mimeType,
        size: input.size,
        threadId: input.threadId,
      }),
    }
  );

  const data = await response.json().catch(() => null);
  if (!response.ok || !data?.success) {
    parseDriveError(response, data);
  }

  return data.data as DriveSaveResult;
}

export function setPendingDriveSave(input: SaveAttachmentToDriveInput): void {
  const pending: PendingDriveSave = {
    ...input,
    createdAt: Date.now(),
  };
  localStorage.setItem(PENDING_DRIVE_SAVE_KEY, JSON.stringify(pending));
}

export function getPendingDriveSave(): PendingDriveSave | null {
  const rawValue = localStorage.getItem(PENDING_DRIVE_SAVE_KEY);
  if (!rawValue) return null;

  try {
    const parsed = JSON.parse(rawValue) as PendingDriveSave;
    if (!parsed?.emailId || !parsed?.attachmentId || !parsed?.filename) {
      clearPendingDriveSave();
      return null;
    }

    if (Date.now() - parsed.createdAt > MAX_PENDING_AGE_MS) {
      clearPendingDriveSave();
      return null;
    }

    return parsed;
  } catch {
    clearPendingDriveSave();
    return null;
  }
}

export function clearPendingDriveSave(): void {
  localStorage.removeItem(PENDING_DRIVE_SAVE_KEY);
}

export function cacheSavedDriveAttachment(
  input: SaveAttachmentToDriveInput,
  result: Pick<DriveSaveResult, 'webViewLink'>
): void {
  const entry: SavedDriveAttachmentCacheEntry = {
    emailId: input.emailId,
    attachmentId: input.attachmentId,
    filename: input.filename,
    mimeType: input.mimeType,
    size: input.size,
    threadId: input.threadId,
    webViewLink: result.webViewLink ?? null,
    savedAt: Date.now(),
  };

  const cache = loadSavedDriveAttachmentCache();
  cache[getExactSaveCacheKey(input)] = entry;

  const threadKey = getThreadFingerprintKey(input);
  if (threadKey) {
    cache[threadKey] = entry;
  }

  persistSavedDriveAttachmentCache(cache);
}

export function getCachedSavedDriveAttachment(
  input: SaveAttachmentToDriveInput
): CachedDriveAttachmentSave | null {
  const cache = loadSavedDriveAttachmentCache();
  const exactMatch = cache[getExactSaveCacheKey(input)];
  if (exactMatch) {
    return {
      webViewLink: exactMatch.webViewLink,
      savedAt: exactMatch.savedAt,
    };
  }

  const threadKey = getThreadFingerprintKey(input);
  if (!threadKey) {
    return null;
  }

  const threadMatch = cache[threadKey];
  if (!threadMatch) {
    return null;
  }

  return {
    webViewLink: threadMatch.webViewLink,
    savedAt: threadMatch.savedAt,
  };
}

export function startDriveConnect(returnUrl: string = getCurrentReturnUrl()): void {
  window.location.href = getDriveConnectUrl(returnUrl);
}

export function startDriveConnectForAttachment(input: SaveAttachmentToDriveInput, returnUrl: string = getCurrentReturnUrl()): void {
  setPendingDriveSave(input);
  startDriveConnect(returnUrl);
}
